import { Typography, styled } from "@mui/material";

import { Column } from "@citrine/atoms/layout";

import { CATALYST_CONTENT_BACKGROUND_COLOR } from "./catalyst.sx";

interface MessageBubbleProps {
  isUser?: boolean;
}

export const MessageBubble = styled("div", {
  shouldForwardProp: (prop) => prop !== "isUser",
})<MessageBubbleProps>(({ isUser, theme }) => ({
  alignSelf: isUser ? "flex-end" : "flex-start",
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  borderRadius: theme.spacing(1.5),
  color: isUser
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  maxWidth: "85%",
  padding: theme.spacing(1, 1.5),
  whiteSpace: isUser ? "pre-wrap" : "normal",

  // Tables use the full panel width (85% is hostile to wide data); very wide
  // tables still scroll horizontally.
  ...(!isUser && {
    "&:has(table)": {
      // Override the base 85% cap so wide tables can use the full panel width.
      maxWidth: "100%",
      width: "100%",
    },
  }),
}));

export const MessageList = styled(Column)(({ theme }) => ({
  background: CATALYST_CONTENT_BACKGROUND_COLOR,
  gap: theme.spacing(1.5),
  overflowY: "auto",
  padding: theme.spacing(2),
  scrollBehavior: "smooth",
}));

// Same shape as EventChip but with error colors.
export const ErrorChip = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  borderRadius: theme.spacing(0.75),
  borderLeft: `3px solid ${theme.palette.error.main}`,
  color: theme.palette.error.dark,
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(0.5, 1),
}));

// Inline chip for a runtime event (tool-call start/end, step progress), on its
// own line as an "activity divider".
export const EventChip = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.spacing(0.75),
  borderLeft: `3px solid ${theme.palette.primary.light}`,
  margin: theme.spacing(0.5, 0),
  opacity: 0.85,
  padding: theme.spacing(0.25, 1),
}));

export const StreamingIndicator = styled("span")(({ theme }) => ({
  animation: "blink 1s steps(2) infinite",
  backgroundColor: theme.palette.text.secondary,
  display: "inline-block",
  height: theme.spacing(1.75),
  verticalAlign: "text-bottom",
  width: theme.spacing(0.75),

  "@keyframes blink": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0 },
  },
}));

export const Text = styled(Typography)({ wordBreak: "break-word" });

export const WelcomeText = styled(Typography)(({ theme }) => ({
  alignSelf: "center",
  color: theme.palette.text.secondary,
  padding: theme.spacing(4, 2),
}));
